import React, { useEffect, useMemo, useState } from 'react';
import {
  ListFilter,
  UsersRound,
  ArrowDownNarrowWide,
  User as UserIcon,
  Scale,
  HandCoins,
  ChevronDown,
  Check,
  CircleDotDashed,
  BanknoteArrowUp,
  ChevronLeft,
  Database,
  ArrowUpNarrowWide,
  ArrowUp,
  ArrowDown,
  Wallet,
  UserRoundPen,
  UserSquare2,
} from 'lucide-react';
import Pagination from '../../../components/ui/pagination/Pagination';
import SearchInput from '../../../components/ui/inputs/SearchInput';
import ClientAccountBadge from '../../../components/ui/badges/ClientAccountBadge';
import ClientBalancingNoteBadge from '../../../components/ui/badges/ClientBalancingNoteBadge';
import CustomButton from '../../../components/ui/buttons/CustomButton';
import CustomCheckbox from '../../../components/ui/checkboxes/CustomCheckbox';
import CustomInput from '../../../components/ui/inputs/CustomInput';
import Button from '../../../components/ui/buttons/Button';
import DashboardTab from '../../planner/client/dashboard/DashboardTab';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import EditAssetModal from '../../../components/modals/assets/EditAssetModal';
import AssetStatusBadge from '../../../components/ui/badges/AssetStatusBadge';
import { formatCNPJ } from '../../../utils/masks';
import ManageBasesModal from '../../../components/modals/assets/ManageBasesModal';
import AddAssetModal from '../../../components/modals/assets/AddAssetModal';
import SortableHeader from '../../../components/ui/table/SortableHeader';
import Select from '../../../components/ui/selects/Select';
import { cn } from '../../../utils/classnames';
import { useToast } from '../../../components/ui/toast/ToastContext';
import ColorSelect from '../../../components/ui/selects/ColorSelect';
import CustomSelect from '../../../components/ui/selects/CustomSelect';
import Input from '../../../components/ui/inputs/Input';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';

const WalletBadgeList = ({ wallets, type }) => {
  const enabledWallets = wallets?.filter((w) => w.walletType === type && w.enabled) || [];

  if (enabledWallets.length === 0) return null;

  const visible = enabledWallets.slice(0, 2);
  const remaining = enabledWallets.length - 2;

  return (
    <div className="flex items-center gap-2">
      {visible.map((w) => (
        <span
          key={w.id}
          style={{ fontSize: '10px', maxWidth: '150px' }}
          className="bg-blueWhisper text-blueMedium px-3 py-1 rounded-full font-semibold uppercase tracking-wider truncate"
        >
          {w.name}
        </span>
      ))}
      {remaining > 0 && (
        <span
          style={{ fontSize: '10px' }}
          className="bg-blueWhisper text-blueMedium px-2 py-1 rounded-full font-semibold uppercase tracking-wider"
        >
          +{remaining}
        </span>
      )}
    </div>
  );
};

const SuitabilityProfiles = () => {
  const [suitabilityProfiles, setSuitabilityProfiles] = useState([]);
  const [selectedSuitabilityProfileId, setSelectedSuitabilityProfileId] = useState(null);
  const [currentSuitabilityProfile, setCurrentSuitabilityProfile] = useState(null);
  const [currentSuitabilityProfileForm, setCurrentSuitabilityProfileForm] = useState({
    name: '',
    colorHex: '',
    riskProfile: '',
    walletProfilesAttributes: [],
  });
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const [riskProfileOptions, setRiskProfileOptions] = useState([]);

  const fetchData = () => {
    fetch('/app/analyst/wallet_configs/suitability_profiles')
      .then((res) => res.json())
      .then((data) => {
        setSuitabilityProfiles(camelizeKeys(data).suitabilityProfiles);
        setSelectedSuitabilityProfileId(camelizeKeys(data).suitabilityProfiles[0]?.id || null);
        setRiskProfileOptions(camelizeKeys(data).riskProfileOptions || []);
      })
      .catch((err) => {
        console.error('Erro ao carregar dados', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados' });
      });
  };

  const fetchSuitabilityProfileDetails = (profileId) => {
    setLoading(true);
    fetch(`/app/analyst/wallet_configs/suitability_profiles/${profileId}`)
      .then((res) => res.json())
      .then((data) => {
        const suitabilityProfile = camelizeKeys(data).suitabilityProfile;
        setCurrentSuitabilityProfile(suitabilityProfile);
        setCurrentSuitabilityProfileForm({
          name: suitabilityProfile.name,
          colorHex: suitabilityProfile.colorHex,
          riskProfile: suitabilityProfile.riskProfile,
          walletsAttributes: suitabilityProfile.walletProfiles,
        });
      })
      .catch((err) => addToast({ type: 'error', message: 'Erro ao carregar detalhes' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedSuitabilityProfileId) {
      fetchSuitabilityProfileDetails(selectedSuitabilityProfileId);
    } else {
      setCurrentSuitabilityProfile(null);
    }
  }, [selectedSuitabilityProfileId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentSuitabilityProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDiscard = () => {
    if (selectedSuitabilityProfileId) {
      fetchSuitabilityProfileDetails(selectedSuitabilityProfileId);
      addToast({ type: 'info', message: 'Alterações descartadas.' });
    }
  };

  const handleSave = async () => {
    setLoading(true);

    const payload = snakecaseKeys({ suitabilityProfile: currentSuitabilityProfileForm });

    try {
      const res = await fetchWithCsrf(
        `/app/analyst/wallet_configs/suitability_profiles/${selectedSuitabilityProfileId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Falha ao salvar');

      addToast({ type: 'success', message: 'Perfil atualizado com sucesso!' });

      fetchData();
      fetchSuitabilityProfileDetails(selectedSuitabilityProfileId);
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Erro ao salvar alterações.' });
    } finally {
      setLoading(false);
    }
  };

  const tableGridColumns = 'minmax(180px, 1.2fr) 150px minmax(200px, 1.5fr) minmax(200px, 1.5fr)';

  return (
    <div className="p-0 flex flex-col h-full">
      <div className="grid gap-5 h-full" style={{ gridTemplateColumns: '400px minmax(0, 1fr)' }}>
        {/* Coluna 1 */}
        <div className="bg-white p-7 rounded-custom20 gap-5 flex flex-col h-fit">
          <span className="flex items-center gap-2">
            <UsersRound className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Perfis de Suitability</h1>
          </span>
          <div className="space-y-4">
            {suitabilityProfiles.map((profile) => (
              <div
                key={profile.id}
                className={cn(
                  'flex flex-row gap-2 items-center p-4 bg-blueLighter rounded-lg',
                  selectedSuitabilityProfileId === profile.id
                    ? 'bg-blueLight'
                    : 'bg-transparent hover:bg-blueUltraLight',
                  'cursor-pointer'
                )}
                onClick={() => setSelectedSuitabilityProfileId(profile.id)}
                style={{ borderRadius: '10px' }}
              >
                <span
                  className="flex-none block rounded"
                  style={{ backgroundColor: profile.colorHex, width: '12px', height: '12px' }}
                />
                <h2 className="text-lg font-semibold text-blueDarker">{profile.name}</h2>
              </div>
            ))}
          </div>
        </div>

        {/* Coluna 2 */}
        <div className="bg-white p-7 rounded-custom20 gap-5 flex flex-col h-full min-h-0">
          {currentSuitabilityProfile ? (
            <>
              <div className="flex items-center gap-2">
                <UserRoundPen className="text-tealMedium" size={20} strokeWidth={1.5} />
                <h1 className="text-2xl font-semibold text-blueDarker">Editar Perfil de Suitability</h1>
                <div className="bg-blueLight px-3 py-1 text-xl text-blueDarker font-normal flex items-center gap-3 rounded-lg">
                  <span
                    className="flex-none block rounded"
                    style={{ backgroundColor: currentSuitabilityProfile.colorHex, width: '12px', height: '12px' }}
                  />
                  <span className="font-normal">{currentSuitabilityProfile.name}</span>
                  {currentSuitabilityProfile.grade && (
                    <span
                      className="bg-white text-sm text-center"
                      style={{ width: '20px', height: '20px', borderRadius: '4px' }}
                    >
                      {currentSuitabilityProfile.grade}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-12 gap-5 w-full items-start pr-2">
                <div className="col-span-6">
                  <Input
                    name="name"
                    label="Nome do Perfil"
                    value={currentSuitabilityProfileForm.name || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-span-3">
                  <ColorSelect
                    name="colorHex"
                    label="Cor da Legenda"
                    value={currentSuitabilityProfileForm.colorHex || ''}
                    onChange={handleChange}
                    withText={true}
                  />
                </div>

                <div className="col-span-3">
                  <CustomSelect
                    name="riskProfile"
                    label="Nível de Risco"
                    options={riskProfileOptions.map((option) => ({ value: option.value, label: option.label }))}
                    value={currentSuitabilityProfileForm.riskProfile || ''}
                    onChange={() => {}}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="flex gap-2 items-center mt-2">
                <UserSquare2 size={20} className="text-tealMedium" strokeWidth={1.5} />
                <span className="text-xl font-semibold text-blueDarker">
                  Perfis de Carteira habilitados para esse perfil:
                </span>
              </div>

              <div className="pr-2 custom-scrollbar overflow-y-auto flex-1 min-h-0">
                {/* Cabeçalho da Tabela */}
                <div className="px-3">
                  <div
                    className="grid items-center gap-4 text-blueDarker font-semibold px-1 py-3"
                    style={{ gridTemplateColumns: tableGridColumns }}
                  >
                    <div className="truncate">Perfil de Carteira</div>
                    <div className="truncate">Nível de Risco</div>
                    <div className="truncate">Carteiras de Investimento</div>
                    <div className="truncate">Carteiras de Previdência</div>
                  </div>
                </div>

                {/* Linhas da Tabela */}
                <div className="space-y-1">
                  {currentSuitabilityProfile.walletProfilesAttributes?.map((wp, idx) => {
                    const isEvenRow = idx % 2 === 0;
                    return (
                      <div
                        key={wp.id}
                        className={`transition-colors rounded-xl ${isEvenRow ? 'bg-blueLight' : 'bg-transparent'}`}
                      >
                        <div
                          className="grid items-center gap-4 px-4 py-4"
                          style={{ gridTemplateColumns: tableGridColumns }}
                        >
                          {/* Nome do Perfil */}
                          <div
                            className="min-w-0 flex items-center gap-3 font-semibold text-blueDarker"
                            title={wp.name}
                          >
                            <span
                              className="flex-none block rounded"
                              style={{ backgroundColor: wp.colorHex, width: '12px', height: '12px' }}
                            />
                            <span className="min-w-0 truncate text-[15px]">{wp.name}</span>
                          </div>

                          {/* Risco */}
                          <div className="truncate" title={wp.riskLabel}>
                            {wp.riskLabel}
                          </div>

                          {/* Badges Investimento */}
                          <div className="flex items-center min-w-0">
                            <WalletBadgeList wallets={wp.walletsAttributes} type="investment" />
                          </div>

                          {/* Badges Previdencia */}
                          <div className="flex items-center min-w-0">
                            <WalletBadgeList wallets={wp.walletsAttributes} type="pension" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <CustomButton
                  onClick={handleDiscard}
                  fontSize="text-sm"
                  bgColor="bg-blueUltraLight"
                  borderColor="border-graySemiDark"
                  className="text-grayDark"
                >
                  DESCARTAR MUDANÇAS
                </CustomButton>
                <CustomButton onClick={handleSave} fontSize="text-sm" disabled={loading}>
                  {loading ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
                </CustomButton>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <UsersRound size={48} className="mb-4 opacity-20" />
              <p>Selecione um perfil de suitability para visualizar e editar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuitabilityProfiles;
